/**
 * Internal semantic icon registry — maps intent-based names directly to Google Material Symbol names.
 *
 * Use these names with the internal <Icon> component. The keys describe *what* the icon means;
 * the values are the Google Material Symbol strings rendered by the font.
 *
 * @example
 * <Icon name="email" />
 * <Icon name="chevronDown" />
 * <Icon name="sentimentPositive" />
 */
export const semanticIcons = {
  // --- Navigation ---
  dockToRight: 'dock_to_right',
  viewSidebar: 'view_sidebar',
  arrowBack: 'arrow_back',
  arrowForward: 'arrow_forward',
  arrowDown: 'arrow_downward',
  arrowUp: 'arrow_upward',
  arrowRight: 'arrow_right_alt',
  subdirectoryArrowRight: 'subdirectory_arrow_right',
  compareArrows: 'compare_arrows',
  arrowDropDown: 'arrow_drop_down',
  arrowDropUp: 'arrow_drop_up',
  arrowCircleDown: 'arrow_circle_down',
  arrowSplit: 'arrow_split',
  callSplit: 'call_split',
  chevronLeft: 'chevron_left',
  chevronRight: 'chevron_right',
  chevronDown: 'keyboard_arrow_down',
  chevronUp: 'keyboard_arrow_up',
  keyboardArrowLeft: 'keyboard_arrow_left',
  keyboardArrowRight: 'keyboard_arrow_right',
  doubleArrowLeft: 'keyboard_double_arrow_left',
  doubleArrowRight: 'keyboard_double_arrow_right',
  expandMore: 'expand_more',
  expandLess: 'expand_less',
  unfoldMore: 'unfold_more',
  openInNew: 'open_in_new',
  openInFull: 'open_in_full',
  launch: 'launch',
  exitToApp: 'exit_to_app',
  login: 'login',
  logout: 'logout',
  home: 'home',
  workspaces: 'workspaces',
  swapHoriz: 'swap_horiz',

  // --- Add, Edit, Delete ---
  add: 'add',
  addBox: 'add_box',
  addCircle: 'add_circle',
  addTask: 'add_task',
  autorenew: 'autorenew',
  cached: 'cached',
  close: 'close',
  check: 'check',
  delete: 'delete',
  edit: 'edit',
  editNote: 'edit_note',
  editSquare: 'edit_square',
  changeCircle: 'change_circle',
  save: 'save',
  clear: 'clear',
  remove: 'remove',
  removeCircle: 'remove_circle',
  cancel: 'cancel',
  block: 'block',
  undo: 'undo',
  redo: 'redo',
  refresh: 'refresh',
  bolt: 'bolt',
  lightbulb: 'lightbulb',
  sync: 'sync',
  restart: 'restart_alt',
  replay: 'replay',
  publishWithChanges: 'published_with_changes',
  draw: 'draw',
  stylusNote: 'stylus_note',
  drag: 'drag_indicator',

  // --- Communication ---
  email: 'email',
  mailOutline: 'mail_outline',
  markEmailRead: 'mark_email_read',
  forwardToInbox: 'forward_to_inbox',
  phone: 'phone',
  call: 'call',
  callLog: 'call_log',
  phoneCallback: 'phone_callback',
  phoneCancel: 'phone_cancel',
  phoneForwarded: 'phone_forwarded',
  callEnd: 'call_end',
  callMade: 'call_made',
  callReceived: 'call_received',
  sms: 'sms',
  sendToMobile: 'send_to_mobile',
  chat: 'chat',
  chatBubble: 'chat_bubble',
  comment: 'comment',
  send: 'send',
  reply: 'reply',
  replyAll: 'reply_all',
  forum: 'forum',
  share: 'share',
  iosShare: 'ios_share',
  notifications: 'notifications',
  notificationsActive: 'notifications_active',
  campaign: 'campaign',
  questionAnswer: 'question_answer',
  help: 'help',
  assignment: 'assignment',
  assignmentReturn: 'assignment_return',

  // --- Cases & Processes ---
  folder: 'folder',
  folderOpen: 'folder_open',
  createNewFolder: 'create_new_folder',
  description: 'description',
  note: 'note',
  stickyNote2: 'sticky_note_2',
  attachFile: 'attach_file',
  uploadFile: 'upload_file',
  upload: 'upload_2',
  cloudUpload: 'cloud_upload',
  download: 'download_2',
  image: 'image',
  pictureAsPdf: 'picture_as_pdf',
  filePresent: 'file_present',
  copy: 'content_copy',
  controlPointDuplicate: 'control_point_duplicate',
  duplicate: 'control_point_duplicate',
  copyAll: 'copy_all',
  inventory: 'inventory',
  inventory2: 'inventory_2',
  archive: 'archive',
  unarchive: 'unarchive',
  cleanUp: 'mop',
  pageview: 'pageview',
  preview: 'preview',
  process: 'flowchart',
  merge: 'merge',
  review: 'approval',
  formStep: 'article',
  update: 'update',
  logInteraction: 'contract_edit',
  insertPageBreak: 'insert_page_break',
  segment: 'segment',
  pageHeader: 'page_header',

  // --- Status ---
  info: 'info',
  warning: 'warning',
  warningAmber: 'warning_amber',
  error: 'error',
  errorOutline: 'error_outline',
  success: 'check_circle',
  feedback: 'feedback',
  quiz: 'quiz',
  factCheck: 'fact_check',
  rule: 'rule',
  healthCheck: 'heart_check',
  doNotDisturb: 'do_not_disturb_on',
  dataUsage: 'data_usage',
  pauseCircle: 'pause_circle',
  verified: 'verified',
  taskAlt: 'task_alt',
  assignmentComplete: 'assignment_turned_in',

  // --- Contacts ---
  person: 'person',
  people: 'people',
  accountCircle: 'account_circle',
  personAdd: 'person_add',
  personCheck: 'person_check',
  personOff: 'person_off',
  personRemove: 'person_remove',
  personSearch: 'person_search',
  assigned: 'assignment_ind',
  group: 'group',
  groupAdd: 'group_add',
  manageAccounts: 'manage_accounts',
  recordVoice: 'record_voice_over',
  work: 'work',
  security: 'security',
  contactBook: 'patient_list',

  // --- Location & Maps ---
  location: 'location_on',
  myLocation: 'my_location',
  map: 'map',
  language: 'language',
  globe: 'globe',
  public: 'public',
  satellite: 'satellite_alt',
  travelExplore: 'travel_explore',

  // --- Grids & Charts ---
  barChart: 'bar_chart',
  barChart4Bars: 'bar_chart_4_bars',
  tableView: 'table_view',
  tableChart: 'table_chart',
  dashboardCustomize: 'dashboard_customize',
  trendingUp: 'trending_up',
  accountTree: 'account_tree',
  frontHand: 'front_hand',

  // --- Calendar & Time ---
  alarm: 'alarm',
  timer: 'timer',
  calendar: 'calendar_today',
  dateRange: 'date_range',
  schedule: 'schedule',
  accessTime: 'access_time',
  history: 'history',
  hourglassBottom: 'hourglass_bottom',
  event: 'event',
  eventBusy: 'event_busy',
  manageHistory: 'manage_history',

  // --- Media ---
  play: 'play_arrow',
  pause: 'pause',
  volume: 'volume_up',
  mute: 'volume_off',
  mic: 'mic',
  camera: 'photo_camera',

  // --- Financial ---
  payments: 'paid',
  attachMoney: 'attach_money',
  monetizationOn: 'monetization_on',
  moneyOff: 'money_off',
  currencyExchange: 'currency_exchange',
  savings: 'savings',
  cart: 'shopping_cart',
  creditCard: 'credit_card',
  creditScore: 'credit_score',
  receipt: 'receipt_long',
  receiptShort: 'receipt',

  // --- Text Formatting ---
  formatBold: 'format_bold',
  formatItalic: 'format_italic',
  formatUnderline: 'format_underlined',
  formatSize: 'format_size',
  formatColorText: 'format_color_text',
  formatBorderColor: 'border_color',
  formatClear: 'format_clear',
  formatHighlight: 'format_ink_highlighter',
  formatAlignLeft: 'format_align_left',
  formatAlignCenter: 'format_align_center',
  formatAlignRight: 'format_align_right',
  formatAlignJustify: 'format_align_justify',
  formatIndentIncrease: 'format_indent_increase',
  formatIndentDecrease: 'format_indent_decrease',
  formatQuote: 'format_quote',
  formatListBulleted: 'format_list_bulleted',
  formatListNumbered: 'format_list_numbered',
  formatList: 'format_list_bulleted',
  strikethrough: 'strikethrough_s',
  underline: 'format_underlined',
  textFields: 'text_fields',
  textFormat: 'text_format',
  translate: 'translate',
  shortText: 'short_text',
  subject: 'subject',
  numbers: 'numbers',

  // --- Sentiment ---
  sentimentPositive: 'sentiment_very_satisfied',
  sentimentNeutral: 'sentiment_satisfied',
  sentimentNegative: 'sentiment_dissatisfied',
  sentimentNeutralAlt: 'sentiment_neutral',

  // --- Inputs, Selections, Menus ---
  search: 'search',
  manageSearch: 'manage_search',
  filter: 'filter_list',
  filterOff: 'filter_list_off',
  filterAlt: 'filter_alt',
  settings: 'settings',
  menuBook: 'menu_book',
  moreHoriz: 'more_horiz',
  moreVert: 'more_vert',
  link: 'link',
  link2: 'link_2',
  sell: 'sell',
  tag: 'tag',
  localOffer: 'local_offer',
  label: 'label',
  pin: 'push_pin',
  flag: 'flag',
  flag2: 'flag_2',
  star: 'star',
  favorite: 'favorite',
  visibility: 'visibility',
  visibilityOff: 'visibility_off',
  toggle: 'toggle_on',
  radioChecked: 'radio_button_checked',
  radioUnchecked: 'radio_button_unchecked',
  checkbox: 'check_box',
  checkboxEmpty: 'check_box_outline_blank',
  circle: 'circle',
  circleCircle: 'circle_circle',
  cropSquare: 'crop_square',
  cursor: 'arrow_selector_tool',
  removeSelection: 'remove_selection',
  adsClick: 'ads_click',
  palette: 'palette',
  wallArt: 'wall_art',
  cancelPresentation: 'cancel_presentation',
  zoomIn: 'zoom_in',
  zoomOut: 'zoom_out',
  backspace: 'keyboard_backspace',
  tripOrigin: 'trip_origin',
  viewComfy: 'view_comfy_alt',
  thumbUp: 'thumb_up',
  thumbUpOutline: 'thumb_up_off_alt',
  thumbDown: 'thumb_down_off_alt',

  // --- Semantic ---
  appRegistration: 'app_registration',
  build: 'build',
  code: 'code',
  science: 'science',
  calculate: 'calculate',
  functions: 'functions',
  mathFunction: 'function',
  category: 'category',
  sensors: 'sensors',
  lan: 'lan',
  autoAwesome: 'auto_awesome',
  autoMode: 'auto_mode',
  rocketLaunch: 'rocket_launch',
  installDesktop: 'install_desktop',
  monitor: 'monitor',
  partnerExchange: 'partner_exchange',
  personShield: 'person_shield',
  hub: 'hub',

  // --- Municipal Services ---
  pool: 'pool',
  balance: 'balance',
  cleanHands: 'clean_hands',

  // --- Changelog ---
  newReleases: 'new_releases',

  // --- Business ---
  apartment: 'apartment',
  business: 'business',
  corporateFare: 'corporate_fare',
  license: 'license',
  lock: 'lock',
  key: 'key',
  localShipping: 'local_shipping',
  darkMode: 'dark_mode',
} as const

export type SemanticIconName = keyof typeof semanticIcons

/**
 * Returns the Google Material Symbol string for a given semantic icon name.
 *
 * @example
 * getIconSymbol('email')        // → 'email'
 * getIconSymbol('chevronDown')  // → 'keyboard_arrow_down'
 */
export const getIconSymbol = (name: SemanticIconName): string => semanticIcons[name]

/**
 * Resolves a string to a Material Symbol name if it matches a semantic icon key.
 * Used internally by the Icon component to support both semantic and raw symbol names.
 */
export const resolveSemanticIconName = (input: string): string | undefined =>
  semanticIcons[input as SemanticIconName]
